import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiFilterOutline, mdiClose } from "@mdi/js";
import { Button } from "@heroui/react";
import { FilterPopover, RangeFilterGroup } from "@pro-sto/ui-v2";
import { contractorsStore } from "@/stores/ContractorsStore";
import { popoverStore } from "@/stores/PopoverStore";
import type {
  ContractorsFilters,
  RangeFilter,
} from "@/stores/ContractorsStore";

const POPOVER_ID = "contractors-filter";

const emptyFilters = (): ContractorsFilters => ({
  rating: {},
  revenue: {},
  commission: {},
  pendingPayout: {},
});

/** Swap from/to if from > to to ensure a valid range. */
function validateRange(range: RangeFilter): RangeFilter {
  if (
    range.from !== undefined &&
    range.to !== undefined &&
    range.from > range.to
  ) {
    return { from: range.to, to: range.from };
  }
  return range;
}

const STATUS_OPTIONS = [
  { value: "all" as const, label: "Все" },
  { value: "active" as const, label: "Активные" },
  { value: "inactive" as const, label: "Скрытые" },
];

const FilterPanel: React.FC = observer(() => {
  const [localFilters, setLocalFilters] =
    useState<ContractorsFilters>(emptyFilters);
  const [localStatus, setLocalStatus] = useState<"all" | "active" | "inactive">(
    "all",
  );

  const updateRange = (
    field: keyof ContractorsFilters,
    key: "from" | "to",
    value: number,
  ) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [key]: isNaN(value) ? undefined : value,
      } as RangeFilter,
    }));
  };

  const handleApply = () => {
    const validated: ContractorsFilters = {
      rating: validateRange(localFilters.rating),
      revenue: validateRange(localFilters.revenue),
      commission: validateRange(localFilters.commission),
      pendingPayout: validateRange(localFilters.pendingPayout),
    };
    contractorsStore.setFilters(validated, localStatus);
  };

  const handleReset = () => {
    setLocalFilters(emptyFilters());
    setLocalStatus("all");
    contractorsStore.resetFilters();
    popoverStore.close(POPOVER_ID);
  };

  return (
    <div data-testid="contractors-filter">
    <FilterPopover
      triggerContent={<Icon path={mdiFilterOutline} size={0.85} />}
      closeIcon={<Icon path={mdiClose} size={0.7} />}
      isActive={contractorsStore.hasActiveFilters}
      isDisabled={contractorsStore.isUiBusy}
      isOpen={popoverStore.isOpen(POPOVER_ID)}
      onOpenChange={(open) => {
        if (open) popoverStore.open(POPOVER_ID);
        else popoverStore.close(POPOVER_ID);
      }}
      onOpen={() => {
        setLocalFilters({ ...contractorsStore.filters });
        setLocalStatus(contractorsStore.statusFilter);
      }}
      onApply={handleApply}
      onReset={handleReset}
    >
      <div data-testid="contractors-filter-panel">
      <div className="sc-filter-panel__group">
        <span className="sc-filter-panel__label">Статус</span>
        <div className="flex gap-1 flex-wrap">
          {STATUS_OPTIONS.map(({ value, label }) => (
            <Button
              key={value}
              size="sm"
              variant={localStatus === value ? "primary" : "tertiary"}
              data-testid={`contractors-filter-status-${value}-button`}
              onPress={() => setLocalStatus(value)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      <RangeFilterGroup
        label="Рейтинг"
        fromValue={localFilters.rating.from}
        toValue={localFilters.rating.to}
        onFromChange={(v: number) => updateRange("rating", "from", v)}
        onToChange={(v: number) => updateRange("rating", "to", v)}
        minValue={0}
        maxValue={5}
        step={0.1}
        formatOptions={{ maximumFractionDigits: 1 }}
      />
      <RangeFilterGroup
        label="Доход, ₽"
        fromValue={localFilters.revenue.from}
        toValue={localFilters.revenue.to}
        onFromChange={(v: number) => updateRange("revenue", "from", v)}
        onToChange={(v: number) => updateRange("revenue", "to", v)}
        minValue={0}
        formatOptions={{ maximumFractionDigits: 0 }}
      />
      <RangeFilterGroup
        label="Комиссия, ₽"
        fromValue={localFilters.commission.from}
        toValue={localFilters.commission.to}
        onFromChange={(v: number) => updateRange("commission", "from", v)}
        onToChange={(v: number) => updateRange("commission", "to", v)}
        minValue={0}
        formatOptions={{ maximumFractionDigits: 0 }}
      />
      <RangeFilterGroup
        label="К оплате, ₽"
        fromValue={localFilters.pendingPayout.from}
        toValue={localFilters.pendingPayout.to}
        onFromChange={(v: number) => updateRange("pendingPayout", "from", v)}
        onToChange={(v: number) => updateRange("pendingPayout", "to", v)}
        minValue={0}
        formatOptions={{ maximumFractionDigits: 0 }}
      />
      </div>
    </FilterPopover>
    </div>
  );
});

FilterPanel.displayName = "FilterPanel";
export default FilterPanel;
